/**
 * The detail panel the layouts share (the concept's drawer): who owns the
 * node, light counts, its risk mix, and what lies directly beneath it.
 *
 * Overlay in three layouts, docked beside the lists in Columns. Its "Open"
 * link is the route to the node's existing detail screen; a context ancestor
 * has none, and says so.
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, X } from 'lucide-react';

import { HolderBox, RiskBar, RiskPill, StatusBadge } from './OverviewParts';
import {
  CHILD_LABEL,
  LEVEL_LABEL,
  detailPath,
  initialsOf,
  pluralise,
  type OrgNode,
  type OrgTree,
} from './overviewModel';

function metricsFor(node: OrgNode): [string, string][] {
  const { counts } = node;
  switch (node.level) {
    case 'group':
      return [
        [String(counts.business_units), 'Business Units'],
        [String(counts.clients), 'Clients'],
        [String(counts.engagements), 'Engagements'],
        [String(counts.active_engagements), 'Active engagements'],
      ];
    case 'unit':
      return [
        [String(counts.clients), 'Clients'],
        [String(counts.engagements), 'Engagements'],
        [String(counts.active_engagements), 'Active engagements'],
        [String(counts.high_risk), 'High-risk engagements'],
      ];
    case 'client':
      return [
        [String(counts.engagements), 'Engagements'],
        [String(counts.active_engagements), 'Active engagements'],
        [String(counts.high_risk), 'High-risk engagements'],
        [String(counts.medium_risk), 'Medium-risk engagements'],
      ];
    case 'engagement':
      return [
        [node.startDate ?? '—', 'Start'],
        [node.endDate ?? '—', 'End'],
      ];
  }
}

export function DetailPanel({
  node,
  tree,
  docked,
  onClose,
  onSelect,
}: {
  node: OrgNode;
  tree: OrgTree;
  docked: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!docked) closeRef.current?.focus({ preventScroll: true });
  }, [docked, node.id]);

  const path = node.ancestors.map((id) => tree.byId.get(id)?.name).filter(Boolean);
  const target = detailPath(node, tree.byId);
  const showsRisk = node.level !== 'engagement';
  const people =
    node.level === 'engagement'
      ? ([['head', node.head]] as const)
      : ([
          ['delivery', node.delivery],
          ['sales', node.sales],
        ] as const);

  return (
    <aside
      className={`ov-drawer${docked ? ' is-docked' : ''}`}
      aria-label={`${LEVEL_LABEL[node.level]} details`}
      role={docked ? 'complementary' : 'dialog'}
    >
      <div className="ov-drawer-head">
        <div>
          <span className="ov-level">{LEVEL_LABEL[node.level]}</span>
          <h3>{node.name}</h3>
          <div className="ov-path">
            <span className="mono">{node.code}</span>
            {path.length > 0 && <> · {path.join(' / ')}</>}
            {!node.isActive && (
              <>
                {' '}
                <StatusBadge node={node} />
              </>
            )}
          </div>
        </div>
        <button ref={closeRef} type="button" className="btn btn-secondary btn-icon" aria-label="Close details" onClick={onClose}>
          <X aria-hidden="true" />
        </button>
      </div>

      <div className="ov-drawer-body">
        {target ? (
          <Link className="btn btn-secondary btn-sm ov-open" to={target}>
            Open {LEVEL_LABEL[node.level]}
            <ArrowUpRight aria-hidden="true" />
          </Link>
        ) : (
          <p className="hint">
            {node.inScope
              ? 'Its Client is outside your scope, so there is no screen to open.'
              : 'Outside your scope. Shown only so the hierarchy reads in order.'}
          </p>
        )}

        {node.inScope && (
          <div className="ov-people">
            {people.map(([kind, holder]) => (
              <div className="ov-pcard" key={kind}>
                <span className={`ov-mono ov-kind-${kind}`} aria-hidden="true">
                  {holder ? initialsOf(holder.display_name) : '—'}
                </span>
                <HolderBox holder={holder} kind={kind} />
              </div>
            ))}
          </div>
        )}

        {node.inScope && (
          <div className="ov-metrics">
            {metricsFor(node).map(([value, label]) => (
              <div className="ov-metric" key={label}>
                <b>{value}</b>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        {node.level === 'engagement' && node.inScope && (
          <div>
            <p className="ov-sub-h">Risk</p>
            <RiskPill riskLevel={node.riskLevel} />
            {node.services && <p className="hint">{node.services}</p>}
          </div>
        )}

        {showsRisk && node.inScope && (
          <div>
            <p className="ov-sub-h">Risk mix</p>
            <RiskBar node={node} />
            <div className="ov-riskleg">
              <span><i className="h" />{node.counts.high_risk} high</span>
              <span><i className="m" />{node.counts.medium_risk} medium</span>
              <span><i className="l" />{node.counts.low_risk} low</span>
            </div>
          </div>
        )}

        {node.children.length > 0 && (
          <div>
            <p className="ov-sub-h">{CHILD_LABEL[node.level]}</p>
            <ul className="ov-elist">
              {node.children.map((child) => (
                <li key={child.id}>
                  <button type="button" className="ov-erow" onClick={() => onSelect(child.id)}>
                    <span className="t">{child.name}</span>
                    <span className="m">
                      {child.level === 'engagement' ? (
                        <RiskPill riskLevel={child.riskLevel} />
                      ) : (
                        <>
                          {child.delivery && <span>{child.delivery.display_name}</span>}
                          <span>{pluralise(child.counts.engagements, 'engagement')}</span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}
